import { useGetIdentity, useLogin } from "@refinedev/core";
import { GoogleLogin, type CredentialResponse } from "@react-oauth/google";
import { Alert, Button, Card, Layout, Space, Spin, Typography } from "antd";
import { useState, type FC } from "react";
import { useSearchParams } from "react-router";
import { axiosInstance } from "../api/client";
import type { User } from "../types";

const { Text, Title } = Typography;

const isLocalhost = (url: string) => {
  try {
    const { hostname } = new URL(url);
    return hostname === "localhost" || hostname === "127.0.0.1";
  } catch {
    return false;
  }
};

export const CliAuth: FC = () => {
  const [searchParams] = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl") ?? "";
  const { mutate: login } = useLogin();
  const { data: identity, isLoading } = useGetIdentity<User>();
  const [authorizing, setAuthorizing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleSuccess = (response: CredentialResponse) => {
    if (response.credential) {
      login({
        idToken: response.credential,
        returnTo: `${window.location.pathname}${window.location.search}`,
      });
    }
  };

  const handleAuthorize = async () => {
    if (!callbackUrl || !isLocalhost(callbackUrl)) {
      setError("Invalid callback URL.");
      return;
    }

    setAuthorizing(true);
    setError(null);

    try {
      const { data } = await axiosInstance.post<{ rawToken: string }>("/tokens", {
        label: "CLI",
      });

      const redirect = new URL(callbackUrl);
      redirect.searchParams.set("token", data.rawToken);
      if (identity?.email) redirect.searchParams.set("email", identity.email);

      window.location.href = redirect.toString();
    } catch {
      setError("Failed to generate token. Please try again.");
      setAuthorizing(false);
    }
  };

  if (isLoading) {
    return (
      <Layout style={centeredLayout}>
        <Spin size="large" />
      </Layout>
    );
  }

  if (!callbackUrl || !isLocalhost(callbackUrl)) {
    return (
      <Layout style={{ ...centeredLayout, background: "#f5f5f5" }}>
        <Card style={{ width: 400, textAlign: "center" }}>
          <Alert
            type="error"
            showIcon
            message="Invalid request"
            description="This page can only be opened by the Loopress CLI."
          />
        </Card>
      </Layout>
    );
  }

  return (
    <Layout style={{ ...centeredLayout, background: "#f5f5f5" }}>
      <Card style={{ width: 400, textAlign: "center" }}>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              Authorize Loopress CLI
            </Title>
            <Text type="secondary">
              The CLI is requesting access to your account
            </Text>
          </div>

          {!identity ? (
            <>
              <Text type="secondary">Sign in to continue</Text>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => undefined}
                  shape="rectangular"
                  size="large"
                  width={280}
                />
              </div>
            </>
          ) : (
            <>
              <Text>
                Signed in as <Text strong>{identity.email}</Text>
              </Text>
              {error && <Alert type="error" message={error} showIcon />}
              <Button
                type="primary"
                size="large"
                block
                loading={authorizing}
                onClick={handleAuthorize}
              >
                Authorize
              </Button>
              <Text type="secondary" style={{ fontSize: 12 }}>
                This will create an API token stored in your CLI config.
              </Text>
            </>
          )}
        </Space>
      </Card>
    </Layout>
  );
};

const centeredLayout: React.CSSProperties = {
  height: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};
